import { Injectable, InjectionToken, Injector } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { IFiHttpPromise, toFiPromise } from '@fi-shared/to-fi-promise';
import { map, skipWhile } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { ActionFailureReasonCode } from '@fi-shared/types/action-failed-reason-code';
import { ILightWeightObject } from '@fi-shared/models';
import { IContactModel } from '@fi-shared/models/common';
import { ObjectTypes } from '@fi-shared/types';

export interface IBackendErrorAgreementContentData {
    UserId: number;
    UserPreferredContentLanguageId: number;
    DefaultContentLanguageId: number;
}

export interface IBackendErrorInactiveUnitData {
    PreferredBusinessUnitId?: number;
    BusinessUnits?: ILightWeightObject[];
    Token?: string;
}

export interface IBackendErrorUserEmailInconsistentData {
    UserId: number;
    AvailableEmails: IContactModel[];
    UnavailableEmails: IContactModel[];
    LoginIsEmail: boolean;
    EnteredLogin: string;
}

export interface IBackendErrorContentDetails
    extends IBackendErrorInactiveUnitData,
        IBackendErrorUserEmailInconsistentData {
    // common properties
    ObjectId?: number[];
    ObjectType?: ObjectTypes;
    ItemsCount?: number;
    Details?: {
        Reason: number;
        Id: number;
        Name: string;
    }[];

    // custom properties
    ErrorMessage?: string;
    ErrorMessages?: string[];
    From?: string;

    // confidential agreement
    UserId: number;
    UserPreferredContentLanguageId: number;
    DefaultContentLanguageId: number;

    // upgrade
    Id?: number;
    UpgradeOn?: string; //ISO 8601 Date
    UpgradeEndTime?: string; //ISO 8601 Date
    SecondsUntilUpgradeStart?: number;
    SecondsUntilUpgradeEnd?: number;
    UpgradeStatusType?: number;
    CustomerInformation?: string;

    // onboardings
    OnboardingIds?: number[];
}

export interface IBackendErrorContentData {
    Reason?: ActionFailureReasonCode;
    Description?: {
        HeaderText?: string;
        MessageText?: string;
        ReasonCodeStr?: string;
    };
    Data?: IBackendErrorContentDetails;
    Names?: string; // todo
    AdditionalCode?: number; // todo
}

export interface IBackendErrorData {
    url?: string;
    error: IBackendErrorContentData;
    status: number;
}

export interface IBackendUploadProgress {
    loaded: number;
    total: number;
}

interface IBackendRequestReqConfig {
    headers?: HttpHeaders;
    params?:
        | HttpParams
        | {
              [param: string]: string | string[];
          };
    reportProgress?: boolean;
    responseType?: any;
    observe?: any;
}

export interface IBackendRequestOptions {
    supressErrorMessage?: boolean;
    forceShowError?: boolean;
    fallbackBackendUrl?: string;
    reqConfig?: IBackendRequestReqConfig;
    absolutePath?: boolean;
    skipContentTypeHeader?: boolean;
    addTokenToUrl?: boolean; // we need token for custom buttons
    onUploadProgress?: (uploadProgress: IBackendUploadProgress) => void;
}

export interface IFiBackendConfig {
    basePath: string;
    token: string;
    getToken: () => string;
}

export const FI_BACKEND_CONFIG = new InjectionToken<IFiBackendConfig>('backendToken');
export const FI_BACKEND_IS_ONLINE_ACCESSOR = new InjectionToken<() => boolean>('FI_BACKEND_IS_ONLINE_ACCESSOR');

@Injectable()
export class FiBackendService {
    private _onHttpError$ = new Subject<IBackendErrorData>();
    get onHttpError$() {
        return this._onHttpError$.asObservable();
    }

    constructor(
        private http: HttpClient,
        private injector: Injector,
    ) {}

    getRequestUrl(path, opts?: IBackendRequestOptions) {
        opts = opts || {};

        if (opts.absolutePath) return path;

        const basePath = (() => {
            if (opts.fallbackBackendUrl) return opts.fallbackBackendUrl;
            return this.injector.get(FI_BACKEND_CONFIG).basePath;
        })();

        if (opts.addTokenToUrl) {
            const token = this.injector.get(FI_BACKEND_CONFIG).getToken
                ? this.injector.get(FI_BACKEND_CONFIG).getToken()
                : this.injector.get(FI_BACKEND_CONFIG).token;

            path = 'token/' + token + '/' + path;
        }

        return basePath + '/' + path;
    }

    private addAuthorizationHeaders(reqConfig: IBackendRequestReqConfig) {
        const token = this.injector.get(FI_BACKEND_CONFIG).getToken
            ? this.injector.get(FI_BACKEND_CONFIG).getToken()
            : this.injector.get(FI_BACKEND_CONFIG).token;

        if (!reqConfig.headers) reqConfig.headers = new HttpHeaders();

        if (token) {
            reqConfig.headers = reqConfig.headers.set('Authorization', 'Basic ' + token);
        }
    }

    ignoreHttpError = false;
    private onHttpError(response: IBackendErrorData, opts: IBackendRequestOptions) {
        if (!this.ignoreHttpError && !opts.supressErrorMessage && (response.status !== -1 || opts.forceShowError))
            this._onHttpError$.next(response);
    }

    fetchData<T>(path: string, opts?: IBackendRequestOptions): IFiHttpPromise<T> {
        return this.fetchDataInternal(path, opts, (url, reqConfig) => this.http.get(url, reqConfig));
    }

    fetchDataPost<T>(path: string, data?, opts?: IBackendRequestOptions): IFiHttpPromise<T> {
        return this.fetchDataInternal(path, opts, (url, reqConfig) => this.http.post(url, data, reqConfig), data);
    }

    private fetchDataInternal<T>(
        path: string,
        opts: IBackendRequestOptions,
        httpRequestCallback: (url: string, reqConfig: IBackendRequestReqConfig) => Observable<any>,
        postBody?: any,
    ) {
        opts = opts || {};

        const url = this.getRequestUrl(path, opts);
        const reqConfig = opts.reqConfig || {};

        if (!reqConfig.observe) {
            {
                reqConfig.observe = 'response';
                reqConfig.responseType = 'json';
            }
        }
        if (!reqConfig.headers) {
            reqConfig.headers = new HttpHeaders();
        }
        if (!this.isExternalApiUrl(url)) {
            this.addAuthorizationHeaders(reqConfig);
        }

        const skipContentTypeHeader = postBody instanceof FormData || opts?.skipContentTypeHeader;
        if (!skipContentTypeHeader) {
            reqConfig.headers = reqConfig.headers.set('Content-Type', 'application/json');
        }

        const request = httpRequestCallback(url, reqConfig)
            .pipe(
                skipWhile((v) => {
                    if (v instanceof HttpResponse) return false;

                    if (v.type === HttpEventType.UploadProgress && opts.onUploadProgress) {
                        opts.onUploadProgress(v);
                    }
                    return true;
                }),
            )
            .pipe(map((response: any) => response.body));
        // 304 status code angular interprets as error so we should handle this situation in FiHttpInterceptorCache
        const result = toFiPromise<T>(request);
        result.catch((response) => this.onHttpError(response, opts));
        return result;
    }

    fetchObjectProj<T>(path: string, projFn: (item) => T, opts?: IBackendRequestOptions) {
        return new Promise<T>((resolve, reject) => {
            this.fetchData<T>(path, opts).then((data) => {
                resolve(data ? projFn(data) : data);
            }, reject);
        });
    }

    fetchObject<T>(path: string, prototype: { new (...args: any[]): T }, opts?: IBackendRequestOptions) {
        return new Promise<T>((resolve, reject) => {
            this.fetchData<T>(path, opts).then((data) => {
                resolve(data ? new prototype(data, this.injector) : data);
            }, reject);
        });
    }

    fetchObjects<T>(path: string, prototype: { new (...args): T }, opts?: IBackendRequestOptions) {
        const fetchPromise = this.fetchData<T[]>(path, opts);
        return this.fetchObjectsFromRequest<T>(fetchPromise, prototype);
    }

    fetchObjectsPost<T>(path: string, prototype: { new (...args): T }, postData, opts?: IBackendRequestOptions) {
        const fetchPromise = this.fetchDataPost<T[]>(path, postData, opts);
        return this.fetchObjectsFromRequest<T>(fetchPromise, prototype);
    }

    private fetchObjectsFromRequest<T>(req: Promise<T[]>, prototype: { new (...args): T }): Promise<T[]> {
        return new Promise<T[]>((resolve, reject) => {
            req.then((response) => {
                const result = response.map((data) => new prototype(data, this.injector));
                resolve(result);
            }, reject);
        });
    }

    public isExternalApiUrl(url: string) {
        const externalSites = ['vimeo.com', 'giphy.com', 'upcloudobjects.com', 'personalkollen.se'];
        return typeof url === typeof '' && externalSites.some((s) => url.indexOf(s) !== -1);
    }
}
